import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import { Box, Button, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { ManualLoader, Wrapper } from "../../../components";
import { WizardShell } from "../../../components/Layouts/WizardShell";
import { StepNavigation } from "../../Account/Register/StepNavigation";
import { RegisterReviewContainer } from "../../Account/Register/Register.style";
import {
  AuthorizationToAssist,
  AuthorizationToAssistData,
} from "./steps/AuthorizationToAssist";
import { CoApplicantIdentity } from "./steps/CoApplicantIdentity";
import { RentalDetails } from "./steps/RentalDetails";
import { StudentDetails, StudentDetailsForm } from "./steps/StudentDetails";
import { SpecialNeeds, SpecialNeedsForm } from "./steps/SpecialNeeds";
import { SpecialStatus } from "./steps/SpecialStatus";
import { HouseholdIncome } from "./steps/HouseholdIncome";
import { Assets } from "./steps/Assets";
import { FinancialSummary } from "./steps/FinancialSummary";
import { UploadCenter } from "./steps/UploadCenter";
import { ReviewSign } from "./steps/ReviewSign";
import { ApplicationData, ApplicationRecord } from "./applicationState";
import {
  CO_APPLICANT_STEP_ORDER,
  computeCoApplicantResume,
} from "./coApplicantState";
import { useUnsavedAffordableStepNavigation } from "./stepNavigationGuard";
import {
  useGetApplicationsMutation,
  useSaveApplicationDataMutation,
  useSubmitAffordableApplicationMutation,
} from "../../../store/services/privateApi";
import { useGetIncomeLimitsQuery } from "../../../store/services/publicApi";
import { securityOptions } from "../../../shared/config/storeConfig";

interface PropertyContact {
  phone?: string;
  email?: string;
  name?: string;
  // Per-property: when true, the Upload Center doesn't block submission.
  affordableDocumentUploadOptional?: boolean;
}

interface ApplicationRecordWithLease extends ApplicationRecord {
  unit?: string;
  moveInDate?: string;
  property?: { id: string; name?: string };
  lease?: { id: string; isAffordable?: boolean };
  primaryStudentDetails?: StudentDetailsForm;
  primarySpecialNeeds?: SpecialNeedsForm;
  primaryMinors?: Array<{ firstName: string; lastName: string }>;
}

export const CoApplicantApplication = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const applicationId = searchParams.get("applicationId") || "";
  const propertyId = searchParams.get("propertyId") || "";

  const steps = [
    t("affordable.application.steps.authorizationToAssist"),
    t("affordable.application.coApplicant.steps.identity"),
    t("affordable.application.steps.rentalDetails"),
    t("affordable.application.steps.studentStatus"),
    t("affordable.application.steps.specialNeeds"),
    t("affordable.application.steps.specialStatus"),
    t("affordable.application.steps.householdIncome"),
    t("affordable.application.steps.assets"),
    t("affordable.application.steps.financialSummary"),
    t("affordable.application.steps.uploadCenter"),
    t("affordable.application.steps.reviewSign"),
  ];

  const [getApplications, { isLoading: isFetching }] =
    useGetApplicationsMutation();
  const [saveApplicationData, { isLoading: isSaving }] =
    useSaveApplicationDataMutation();
  const [submitAffordable, { isLoading: isSubmitting }] =
    useSubmitAffordableApplicationMutation();
  const { data: propertyData } = useGetIncomeLimitsQuery(propertyId, {
    skip: !propertyId,
  }) as { data: { property?: PropertyContact } | undefined };
  const propertyContact = propertyData?.property;

  const [hasHydrated, setHasHydrated] = useState(false);
  const [applicationData, setApplicationData] = useState<ApplicationData>({});
  const [appRecord, setAppRecord] = useState<ApplicationRecordWithLease | null>(
    null
  );
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const unsavedStepNavigationMessage = t(
    "affordable.application.unsavedStepNavigation"
  ) as string;
  const { navigateBack: handleBack, navigateToStep: handleStepClick } =
    useUnsavedAffordableStepNavigation({
      currentStep,
      completedSteps,
      setCurrentStep,
      setCompletedSteps,
      confirmMessage: unsavedStepNavigationMessage,
    });

  useEffect(() => {
    if (!applicationId) {
      setHasHydrated(true);
      return;
    }
    getApplications({ id: applicationId, propertyId }).then((res) => {
      const list = (res as { data?: ApplicationRecordWithLease[] }).data;
      const record = list?.find((a) => a.id === applicationId);
      const data = record?.applicationData || {};
      setApplicationData(data);
      setAppRecord(record || null);
      // Inherit primary's language if recorded
      if (data.language && data.language !== i18n.language) {
        i18n.changeLanguage(data.language);
      }
      const { completedSteps: completed, resumeStep } =
        computeCoApplicantResume(data);
      setCompletedSteps(new Set(completed));
      setCurrentStep(resumeStep);
      if (record?.status === "submitted") setIsSubmitted(true);
      setHasHydrated(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, propertyId]);

  const handleStepSubmit = async (stepIndex: number, stepData: unknown) => {
    const stepKey = CO_APPLICANT_STEP_ORDER[stepIndex];
    const merged: ApplicationData = {
      ...applicationData,
      [stepKey]: stepData,
      language: i18n.language,
    };
    const res = (await saveApplicationData({
      id: applicationId,
      body: { applicationData: merged },
    })) as { data?: ApplicationRecord };
    if (res.data) {
      setApplicationData(res.data.applicationData);
      setCompletedSteps((prev) => {
        const next = new Set(prev);
        next.add(stepIndex);
        return next;
      });
      if (stepIndex < steps.length - 1) {
        setCurrentStep(stepIndex + 1);
      }
    }
  };

  // Save a partial slice without marking complete or advancing. Mirrors the
  // HoH-view helper in AffordableApplication so UploadCenter can persist
  // incrementally on every upload/delete.
  const handlePartialStepSave = async (
    stepIndex: number,
    stepData: unknown
  ) => {
    const stepKey = CO_APPLICANT_STEP_ORDER[stepIndex];
    const existing =
      (applicationData[stepKey] as Record<string, unknown>) || {};
    const merged: ApplicationData = {
      ...applicationData,
      [stepKey]: { ...existing, ...(stepData as Record<string, unknown>) },
      language: i18n.language,
    };
    const res = (await saveApplicationData({
      id: applicationId,
      body: { applicationData: merged },
    })) as { data?: ApplicationRecord };
    if (res.data) {
      setApplicationData(res.data.applicationData);
    }
  };

  const handleSubmitApplication = async (stepData: unknown) => {
    const lastIdx = CO_APPLICANT_STEP_ORDER.length - 1;
    const merged: ApplicationData = {
      ...applicationData,
      [CO_APPLICANT_STEP_ORDER[lastIdx]]: stepData,
      language: i18n.language,
    };
    setSubmitError("");
    const fallbackError = t("affordable.application.reviewSign.submitError");
    const saveRes = (await saveApplicationData({
      id: applicationId,
      body: { applicationData: merged },
    })) as {
      data?: ApplicationRecord;
      error?: { data?: { message?: string } };
    };
    if (saveRes.error || !saveRes.data) {
      setSubmitError(saveRes.error?.data?.message ?? fallbackError);
      return;
    }
    const submitRes = (await submitAffordable({
      id: applicationId,
    })) as {
      data?: { application?: ApplicationRecord };
      error?: { data?: { message?: string } };
    };
    // Surface backend rejections instead of silently swallowing them — mirrors
    // the primary AffordableApplication submit handler.
    if (submitRes.error || !submitRes.data?.application) {
      setSubmitError(submitRes.error?.data?.message ?? fallbackError);
      return;
    }
    const updatedApp = submitRes.data.application;
    setApplicationData(updatedApp.applicationData);
    setCompletedSteps((prev) => {
      const next = new Set(prev);
      next.add(lastIdx);
      return next;
    });
    setIsSubmitted(true);
  };

  if (!applicationId || !propertyId) {
    return (
      <Wrapper>
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h4">
            {t("affordable.application.notFound")}
          </Typography>
        </Box>
      </Wrapper>
    );
  }

  if (isFetching || !hasHydrated) {
    return <ManualLoader />;
  }

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <AuthorizationToAssist
            applicationId={applicationId}
            defaultValues={
              applicationData.authorizationToAssist as
                | AuthorizationToAssistData
                | undefined
            }
            onSubmit={(data) => handleStepSubmit(0, data)}
            isSaving={isSaving}
          />
        );
      case 1:
        return (
          <CoApplicantIdentity
            defaultValues={applicationData.memberProfiles as any}
            onSubmit={(data) => handleStepSubmit(1, data)}
            isSaving={isSaving}
          />
        );
      case 2:
        return (
          <RentalDetails
            defaultValues={applicationData.rentalDetails as any}
            onSubmit={(data) => handleStepSubmit(2, data)}
            onBack={handleBack}
            isSaving={isSaving}
          />
        );
      case 3:
        return (
          <StudentDetails
            defaultValues={applicationData.studentDetails as any}
            onSubmit={(data) => handleStepSubmit(3, data)}
            onBack={handleBack}
            isSaving={isSaving}
            propertyContact={propertyContact}
            primaryStudentDetails={appRecord?.primaryStudentDetails}
          />
        );
      case 4:
        return (
          <SpecialNeeds
            defaultValues={applicationData.specialNeeds as any}
            onSubmit={(data) => handleStepSubmit(4, data)}
            onBack={handleBack}
            isSaving={isSaving}
            primarySpecialNeeds={appRecord?.primarySpecialNeeds}
          />
        );
      case 5:
        return (
          <SpecialStatus
            defaultValues={applicationData.specialStatus as any}
            onSubmit={(data) => handleStepSubmit(5, data)}
            onBack={handleBack}
            isSaving={isSaving}
          />
        );
      case 6: {
        const minors = (appRecord?.primaryMinors ?? [])
          .filter((m) => m.firstName && m.lastName)
          .map((m) => ({
            id: `${m.firstName} ${m.lastName}`.trim(),
            firstName: m.firstName,
            lastName: m.lastName,
          }));
        return (
          <HouseholdIncome
            defaultValues={applicationData.householdIncome as any}
            onSubmit={(data) => handleStepSubmit(6, data)}
            onBack={handleBack}
            isSaving={isSaving}
            householdMinors={minors}
          />
        );
      }
      case 7:
        return (
          <Assets
            defaultValues={applicationData.assets as any}
            onSubmit={(data) => handleStepSubmit(7, data)}
            onBack={handleBack}
            isSaving={isSaving}
          />
        );
      case 8:
        return (
          <FinancialSummary
            applicationData={applicationData}
            onSubmit={(data) => handleStepSubmit(8, data)}
            onBack={handleBack}
            isSaving={isSaving}
          />
        );
      case 9:
        return (
          <UploadCenter
            applicationId={applicationId}
            applicationData={applicationData}
            onSubmit={(data) => handleStepSubmit(9, data)}
            onPartialSave={(data) => handlePartialStepSave(9, data)}
            onBack={handleBack}
            isSaving={isSaving}
            documentUploadOptional={
              propertyContact?.affordableDocumentUploadOptional ?? false
            }
          />
        );
      case 10:
        return (
          <ReviewSign
            applicationData={applicationData}
            onSubmit={handleSubmitApplication}
            onBack={handleBack}
            isSaving={isSubmitting}
            errorMessage={submitError}
            onDismissError={() => setSubmitError("")}
          />
        );
      default:
        return null;
    }
  };

  if (isSubmitted) {
    // Correction mode has no Auth0 session (the editor rode the correction token),
    // so the applicant's "Back to Applications" route would bounce staff to the
    // resident login. Show a terminal "close this tab" screen instead.
    const isCorrection = !!securityOptions.getCorrectionToken();
    return (
      <WizardShell>
        <RegisterReviewContainer>
          <Box sx={{ textAlign: "center", py: 8 }}>
            <CheckCircleOutlineIcon
              color="success"
              sx={{ fontSize: 72, mb: 2 }}
            />
            <Typography variant="h3" sx={{ mb: 1 }}>
              {t(
                isCorrection
                  ? "affordable.application.reviewSign.success.correctionTitle"
                  : "affordable.application.reviewSign.success.title"
              )}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 3, maxWidth: 480, mx: "auto" }}
            >
              {t(
                isCorrection
                  ? "affordable.application.reviewSign.success.correctionMessage"
                  : "affordable.application.reviewSign.success.message"
              )}
            </Typography>
            {isCorrection ? (
              <Button variant="contained" onClick={() => window.close()}>
                {t("affordable.application.reviewSign.success.close")}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={() => navigate("/settings/applications")}
              >
                {t(
                  "affordable.application.reviewSign.success.backToApplications"
                )}
              </Button>
            )}
          </Box>
        </RegisterReviewContainer>
      </WizardShell>
    );
  }

  // Read-only property header
  const propertyName = appRecord?.property?.name || "";
  const unit = appRecord?.unit || "";
  const moveIn = appRecord?.moveInDate
    ? moment(appRecord.moveInDate).format("MM/DD/YYYY")
    : "";
  const headerLine = [
    propertyName,
    unit && `${t("affordable.application.coApplicant.header.unit")} ${unit}`,
    moveIn &&
      `${t("affordable.application.coApplicant.header.moveIn")} ${moveIn}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <WizardShell
      stepper={
        <StepNavigation
          currentStep={currentStep}
          totalSteps={steps.length}
          onStepClick={handleStepClick}
          stepTitles={steps}
          completedSteps={completedSteps}
        />
      }
    >
      <RegisterReviewContainer>
        {headerLine && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 1,
              bgcolor: "action.hover",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {headerLine}
            </Typography>
          </Box>
        )}
        {renderStep()}
      </RegisterReviewContainer>
    </WizardShell>
  );
};
